import type { TFunction } from 'i18next'
import type { DataSource } from '@/src/renderer/lib/dataSource'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import { INTRO_STEPS, introStepText, isLearnTaskOf, learnTaskOrigin, type IntroStepId } from '@/src/renderer/lib/intro'
import { formatImportedFrom } from '@/src/renderer/lib/import/importedFrom'
import { keepingUndo } from '@/src/renderer/lib/dataSource/withUndoForget'
import { blankTask, INTRO_REWARD_ID } from './sampleProject'

/**
 * **Lernaufgaben** (#323, Variante G): Aufgaben in Kurz notiert, die sich selbst erklären — alle Schritte
 * (Sven, 13.10.2026; seit #330 zwölf), oben in fester Reihenfolge, der knappe Text als Beschreibung. „Hake mich ab“ trägt eine
 * Belohnung: Nur dann hält der Geschafft-Moment in der Liste an.
 *
 * Legt nur an, was nicht schon offen daliegt — „Einführung neu starten“ füllt auf, statt zu verdoppeln.
 * Gibt die Zahl der neuen Aufgaben zurück.
 */
export async function writeLearnTasks(source: DataSource, { t, lifeSpace, web, quickAddKeys }: {
  t: TFunction
  lifeSpace: LifeSpace
  web: boolean
  quickAddKeys: string
}): Promise<number> {
  const open = new Set<IntroStepId>()
  for (const [origin, row] of (await source.findImported('intro')).tasks) {
    const step = row.deleted ? undefined : INTRO_STEPS.find(candidate => isLearnTaskOf(origin, candidate))
    if (!step) continue
    const task = await source.getTask(row.id)
    if (task && task.completedAt == null) open.add(step)
  }
  const missing = INTRO_STEPS.filter(step => !open.has(step))
  if (missing.length === 0) return 0

  // Über alles, was schon in Kurz notiert liegt — dort steht oben, was zuletzt kam (#247).
  const notes = await source.getQuickNotes(lifeSpace)
  const base = notes.length ? Math.min(...notes.map(note => note.order)) - missing.length : undefined
  const run = Date.now().toString(36)
  const options = { web, quickAddKeys }
  for (const [index, step] of missing.entries()) {
    await source.createTask({
      ...blankTask(introStepText(t, step, 'learn', options), lifeSpace),
      description: introStepText(t, step, 'short', options),
      rewardId: step === 'done' ? INTRO_REWARD_ID : null,
      projectId: null,
      sectionId: null,
      ...(base == null ? {} : { order: base + index }),
      importedFrom: learnTaskOrigin(step, run),
    })
  }
  return missing.length
}

/** Schritte, die es nicht mehr gibt — ihre Lernaufgaben könnten sich nie mehr abhaken. */
const RETIRED_LEARN_STEPS = ['happymode'] as const

/**
 * Löscht offene Lernaufgaben entfallener Schritte (Happy-Modus, #330). Erledigte bleiben in Geschafft.
 * Still und ohne Rückgängig: Niemand hat sie gerade gelöscht. Gibt die Zahl der gelöschten zurück.
 */
export async function removeRetiredLearnTasks(source: DataSource): Promise<number> {
  const bases = RETIRED_LEARN_STEPS.map(step => formatImportedFrom('intro', `learn:${step}`))
  let removed = 0
  for (const [origin, row] of (await source.findImported('intro')).tasks) {
    if (row.deleted || !bases.some(base => origin === base || origin.startsWith(`${base}:`))) continue
    const task = await source.getTask(row.id)
    if (!task || task.completedAt != null) continue
    await keepingUndo(() => source.deleteTask(task.id))
    removed += 1
  }
  return removed
}
